import type { JSX } from 'react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Input } from '@artosera/ui';
import type {
  ChampRegle,
  ColonnePivot,
  ColonneProfilee,
  Transformation,
  TypeTransformation,
} from '../../../../lib/correspondance.js';
import { TYPES_TRANSFORMATION } from '../../../../lib/correspondance.js';
import { champVide, refVide, transformationParDefaut } from '../../../../lib/reglesEdition.js';
import { RefEditeur } from './RefEditeur.js';
import { TransformationEditeur } from './TransformationEditeur.js';
import reprise from '../Reprise.module.css';
import styles from './Correspondance.module.css';

type ModeSource = 'colonne' | 'plusieurs' | 'chemin';

function modeInitial(champ: ChampRegle): ModeSource {
  if (champ.sources.length > 1) return 'plusieurs';
  const unique = champ.sources[0]?.champ ?? '';
  return /[.[\]]/.test(unique) ? 'chemin' : 'colonne';
}

/**
 * Le volet de droite : d'où vient un champ du pivot, et ce qu'on lui fait
 * subir. La source est une colonne, plusieurs colonnes réunies par un
 * séparateur, ou un chemin dans un fichier JSON ou XML ; les transformations
 * se choisissent dans la liste fermée de l'API.
 */
export function ChampEditeur({
  colonne,
  champ,
  onChange,
  erreurs,
  suggestions,
  chemins,
  roles,
  avecParent,
  colonneAmbigue,
  echantillon,
  onRetour,
}: {
  colonne: ColonnePivot;
  champ: ChampRegle | undefined;
  onChange: (suite: ChampRegle | null) => void;
  erreurs: readonly string[];
  suggestions: readonly string[];
  chemins: readonly string[];
  roles: readonly string[];
  avecParent: boolean;
  /** La colonne source est une date ambiguë : son nom, sinon `null`. */
  colonneAmbigue: string | null;
  /** Le profil de la colonne lue en premier, pour montrer ses valeurs. */
  echantillon: ColonneProfilee | null;
  /** Sous 768 px, retour à la liste des champs. */
  onRetour: () => void;
}): JSX.Element {
  const { t } = useTranslation('console');
  const id = useId();
  const actuel = champ ?? champVide();
  const [mode, setMode] = useState<ModeSource>(modeInitial(actuel));
  const [ajout, setAjout] = useState<TypeTransformation>('majuscules');
  const aUneDate = actuel.transformations.some((etape) => etape.type === 'date');

  const maj = (suite: Partial<ChampRegle>): void => onChange({ ...actuel, ...suite });
  const majSource = (rang: number, source: ChampRegle['sources'][number]): void =>
    maj({ sources: actuel.sources.map((ancienne, i) => (i === rang ? source : ancienne)) });
  const majEtape = (rang: number, etape: Transformation): void =>
    maj({
      transformations: actuel.transformations.map((ancienne, i) => (i === rang ? etape : ancienne)),
    });
  const deplacer = (rang: number, sens: -1 | 1): void => {
    const suite = [...actuel.transformations];
    const [etape] = suite.splice(rang, 1);
    if (etape) suite.splice(rang + sens, 0, etape);
    maj({ transformations: suite });
  };

  const passerEn = (suivant: ModeSource): void => {
    setMode(suivant);
    // Une seule source hors du mode « plusieurs » : on garde la première.
    if (suivant !== 'plusieurs' && actuel.sources.length > 1)
      maj({ sources: actuel.sources.slice(0, 1) });
  };

  const sources = actuel.sources.length === 0 ? [refVide()] : actuel.sources;
  const propositions = mode === 'chemin' ? chemins : suggestions;
  const echantillonVisible = (echantillon?.frequentes ?? []).slice(0, 3);

  return (
    <div className={styles.champEditeur} aria-labelledby={`${id}-titre`}>
      <div className={styles.champEntete}>
        <Button variant="ghost" size="sm" className={styles.retourListe} onClick={onRetour}>
          {t('correspondance.editeur.retourChamps')}
        </Button>
        <h3 id={`${id}-titre`} className={reprise.sousTitre}>
          {colonne.entete}
        </h3>
        {colonne.obligatoire ? (
          <Badge tone="sold">{t('correspondance.editeur.obligatoire')}</Badge>
        ) : (
          <Badge tone="neutral">{t('correspondance.editeur.facultatif')}</Badge>
        )}
      </div>
      {colonne.aide ? <p className={reprise.discret}>{colonne.aide}</p> : null}

      {erreurs.length > 0 ? (
        <ul className={styles.erreursChamp} role="alert">
          {erreurs.map((message) => (
            <li key={message}>{message}</li>
          ))}
        </ul>
      ) : null}

      <fieldset className={styles.fieldset}>
        <legend>{t('correspondance.editeur.origine')}</legend>
        <div
          className={styles.modes}
          role="radiogroup"
          aria-label={t('correspondance.editeur.origine')}
        >
          {(['colonne', 'plusieurs', 'chemin'] as const).map((valeur) => (
            <label key={valeur} className={styles.mode}>
              <input
                type="radio"
                name={`${id}-mode`}
                value={valeur}
                checked={mode === valeur}
                onChange={() => passerEn(valeur)}
              />
              <span>{t(`correspondance.editeur.modes.${valeur}`)}</span>
            </label>
          ))}
        </div>

        {sources.map((source, rang) => (
          <div key={rang} className={styles.ligneSource}>
            <RefEditeur
              valeur={source}
              libelle={
                mode === 'chemin'
                  ? t('correspondance.editeur.chemin')
                  : mode === 'plusieurs'
                    ? t('correspondance.editeur.colonneNumero', { rang: rang + 1 })
                    : t('correspondance.editeur.colonne')
              }
              suggestions={propositions}
              roles={roles}
              avecParent={avecParent}
              invalide={erreurs.length > 0 && source.champ.trim() === ''}
              onChange={(suite) =>
                actuel.sources.length === 0 ? maj({ sources: [suite] }) : majSource(rang, suite)
              }
            />
            {mode === 'plusieurs' && sources.length > 1 ? (
              <Button
                variant="ghost"
                size="sm"
                aria-label={t('correspondance.editeur.retirerColonne', { rang: rang + 1 })}
                onClick={() => maj({ sources: actuel.sources.filter((_, i) => i !== rang) })}
              >
                {t('correspondance.editeur.retirer')}
              </Button>
            ) : null}
          </div>
        ))}

        {mode === 'plusieurs' ? (
          <div className={styles.ligneSeparateur}>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => maj({ sources: [...actuel.sources, refVide()] })}
            >
              {t('correspondance.editeur.ajouterColonne')}
            </Button>
            <div className={styles.parametre}>
              <label htmlFor={`${id}-sep`}>{t('correspondance.editeur.separateurColonnes')}</label>
              <Input
                id={`${id}-sep`}
                value={actuel.separateur}
                maxLength={10}
                onChange={(e) => maj({ separateur: e.target.value })}
              />
            </div>
          </div>
        ) : null}

        {echantillonVisible.length > 0 ? (
          <p className={reprise.discret}>
            {t('correspondance.editeur.exemples', {
              valeurs: echantillonVisible.map((v) => `« ${v.valeur} »`).join(', '),
            })}
          </p>
        ) : null}
      </fieldset>

      {colonneAmbigue !== null ? (
        <div className={`${reprise.avertissement} ${styles.note}`} role="note">
          <strong>
            {t('correspondance.editeur.dateAmbigueTitre', { colonne: colonneAmbigue })}
          </strong>
          <span>{t('correspondance.editeur.dateAmbigueTexte')}</span>
          {!aUneDate ? (
            <div className={reprise.actions}>
              <Button
                variant="secondary"
                size="sm"
                onClick={() =>
                  maj({
                    transformations: [...actuel.transformations, transformationParDefaut('date')],
                  })
                }
              >
                {t('correspondance.editeur.ajouterDate')}
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}

      <div className={reprise.pile}>
        <h4 className={reprise.sousTitre}>{t('correspondance.editeur.transformations')}</h4>
        {actuel.transformations.length === 0 ? (
          <p className={reprise.discret}>{t('correspondance.editeur.aucuneTransformation')}</p>
        ) : (
          <ol className={styles.etapesRegle}>
            {actuel.transformations.map((etape, rang) => (
              <TransformationEditeur
                key={`${String(rang)}-${etape.type}`}
                etape={etape}
                rang={rang}
                total={actuel.transformations.length}
                onChange={(suite) => majEtape(rang, suite)}
                onDeplacer={(sens) => deplacer(rang, sens)}
                onRetirer={() =>
                  maj({ transformations: actuel.transformations.filter((_, i) => i !== rang) })
                }
                contexte={{ suggestions, roles, avecParent }}
                formatManquant={colonneAmbigue !== null}
              />
            ))}
          </ol>
        )}
        <div className={styles.ajoutEtape}>
          <div className={styles.parametre}>
            <label htmlFor={`${id}-ajout`}>{t('correspondance.editeur.nouvelleEtape')}</label>
            <select
              id={`${id}-ajout`}
              className={styles.selection}
              value={ajout}
              onChange={(e) => setAjout(e.target.value as TypeTransformation)}
            >
              {TYPES_TRANSFORMATION.map((type) => (
                <option key={type} value={type}>
                  {t(`correspondance.transformations.${type}`)}
                </option>
              ))}
            </select>
          </div>
          <Button
            variant="secondary"
            onClick={() =>
              maj({ transformations: [...actuel.transformations, transformationParDefaut(ajout)] })
            }
          >
            {t('correspondance.editeur.ajouterEtape')}
          </Button>
        </div>
      </div>

      {champ ? (
        <div className={reprise.actions}>
          <Button variant="ghost" onClick={() => onChange(null)}>
            {t('correspondance.editeur.viderChamp')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
